import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';

// Verbindet den Traumgarten-Planer mit dem Kontaktformular.
// Geführter Modus: plan.items ({ typ: variante }); freier Modus: free.present (Instanzmodell) mit Undo/Redo.
const STORAGE_KEY = 'eg-traumgarten';
const FREE_KEY = 'eg-traumgarten-frei';
// summary: Momentaufnahme für das Kontaktformular ({ lines, text, size }) – vom Planer gepflegt
const EMPTY = { items: {}, size: 'mittel', attached: false, summary: null, mode: 'guided' };
const HISTORY_LIMIT = 100;
const COALESCE_MS = 700;

const GardenPlanContext = createContext(null);

function load() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const data = JSON.parse(raw);
    return { ...EMPTY, ...data, items: { ...(data.items || {}) } };
  } catch {
    return EMPTY;
  }
}

function loadFree() {
  try {
    const raw = window.sessionStorage.getItem(FREE_KEY);
    const design = raw ? JSON.parse(raw) : null;
    return { past: [], present: design && design.version === 1 ? design : null, future: [], coalesce: null };
  } catch {
    return { past: [], present: null, future: [], coalesce: null };
  }
}

// ── Verlauf für den freien Modus ──
function historyReducer(state, action) {
  switch (action.type) {
    case 'init':
      return { past: [], present: action.design, future: [], coalesce: null };
    case 'commit': {
      if (action.design === state.present) return state;
      const now = Date.now();
      const merge = action.coalesce && state.coalesce
        && state.coalesce.key === action.coalesce && now - state.coalesce.at < COALESCE_MS;
      return {
        past: merge ? state.past : [...state.past, state.present].slice(-HISTORY_LIMIT),
        present: action.design,
        future: [],
        coalesce: action.coalesce ? { key: action.coalesce, at: now } : null,
      };
    }
    case 'undo': {
      if (!state.past.length) return state;
      return {
        past: state.past.slice(0, -1),
        present: state.past[state.past.length - 1],
        future: [state.present, ...state.future],
        coalesce: null,
      };
    }
    case 'redo': {
      if (!state.future.length) return state;
      return {
        past: [...state.past, state.present],
        present: state.future[0],
        future: state.future.slice(1),
        coalesce: null,
      };
    }
    default:
      return state;
  }
}

export function GardenPlanProvider({ children }) {
  const [plan, setPlan] = useState(load);
  const [free, dispatch] = useReducer(historyReducer, undefined, loadFree);
  const sketchRef = useRef(null);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
    } catch {
      /* Speicher nicht verfügbar – Entwurf bleibt nur im Speicher */
    }
  }, [plan]);

  useEffect(() => {
    try {
      if (free.present) window.sessionStorage.setItem(FREE_KEY, JSON.stringify(free.present));
    } catch {
      /* Speicher nicht verfügbar */
    }
  }, [free.present]);

  const setItem = useCallback((id, variant) => {
    setPlan((p) => {
      const items = { ...p.items };
      if (variant) items[id] = variant;
      else delete items[id];
      return { ...p, items };
    });
  }, []);

  const setSize = useCallback((size) => setPlan((p) => ({ ...p, size })), []);
  const setItems = useCallback((items) => setPlan((p) => ({ ...p, items: { ...items } })), []);
  const reset = useCallback(() => setPlan((p) => ({ ...EMPTY, size: p.size, mode: p.mode })), []);
  const setSummary = useCallback((summary) => setPlan((p) => ({ ...p, summary })), []);
  const attach = useCallback(() => setPlan((p) => ({ ...p, attached: true })), []);
  const detach = useCallback(() => setPlan((p) => ({ ...p, attached: false })), []);
  const setMode = useCallback((mode) => setPlan((p) => ({ ...p, mode })), []);

  // Planskizze: der Planer registriert eine Funktion, die ein Bild des aktiven Entwurfs erzeugt
  const registerSketch = useCallback((fn) => { sketchRef.current = fn; }, []);
  const getSketch = useCallback(() => (sketchRef.current ? sketchRef.current() : Promise.resolve(null)), []);

  const freeApi = useMemo(() => ({
    design: free.present,
    canUndo: free.past.length > 0,
    canRedo: free.future.length > 0,
    init: (design) => dispatch({ type: 'init', design }),
    commit: (design, coalesce) => dispatch({ type: 'commit', design, coalesce }),
    undo: () => dispatch({ type: 'undo' }),
    redo: () => dispatch({ type: 'redo' }),
  }), [free]);

  const value = useMemo(
    () => ({ plan, setItem, setItems, setSize, setSummary, reset, attach, detach, setMode, free: freeApi, registerSketch, getSketch }),
    [plan, setItem, setItems, setSize, setSummary, reset, attach, detach, setMode, freeApi, registerSketch, getSketch]
  );

  return <GardenPlanContext.Provider value={value}>{children}</GardenPlanContext.Provider>;
}

export function useGardenPlan() {
  return useContext(GardenPlanContext);
}
